import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileText, Search, RotateCcw, Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  DataTable,
  EmptyState,
  Pagination,
  type ColumnDef,
} from '@vnetwork/ui/data';
import {
  Input,
  Badge,
  Skeleton,
  Button,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWafCustomPages,
  useDeleteWafCustomPage,
  wafExtractError,
  type WafCustomPage,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { useDebounce } from '../../shared';
import { CUSTOM_PAGE_TYPES, customPageTypeLabel } from './custom-page-types';
import { CustomPageFormDialog } from './CustomPageFormDialog';
import { CustomPagePreviewDialog } from './CustomPagePreviewDialog';
import styles from './WafCustomPageList.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;
/** Custom-page quota for the service (used = number of pages). */
const QUOTA_MAX = 10;

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

/** Row actions: preview rendered HTML, edit, delete. */
function CustomPageActionsCell({ serviceId, page }: { serviceId: string; page: WafCustomPage }) {
  const { show } = useToast();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteWafCustomPage(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Custom page deleted' }); trackFeatureAction('waf', 'custom_page', 'delete'); setConfirmDelete(false); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Preview" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Preview custom page" onClick={() => setPreviewOpen(true)}>
            <Eye size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Edit" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Edit custom page" onClick={() => setEditOpen(true)}>
            <Pencil size={14} />
          </IconButton>
        </Tooltip>
        <Tooltip content="Delete" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Delete custom page" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>

      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete custom page?"
          description={`Delete the custom page "${page.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate(page.id)}
        />
      )}

      {previewOpen && (
        <CustomPagePreviewDialog
          name={page.name}
          content={page.content}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
        />
      )}

      {editOpen && (
        <CustomPageFormDialog serviceId={serviceId} page={page} open={editOpen} onOpenChange={setEditOpen} />
      )}
    </div>
  );
}

export function WafCustomPageList({ serviceId }: { serviceId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get('page') || '1')));
  const [pageSize, setPageSize] = useState(() => {
    const p = Number(searchParams.get('page_size') || String(DEFAULT_PAGE_SIZE));
    return PAGE_SIZE_OPTIONS.includes(p) ? p : DEFAULT_PAGE_SIZE;
  });
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [typeFilter, setTypeFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);

  // Only pagination is server-driven; search/type filtering is client-side.
  useEffect(() => {
    setSearchParams({ page: String(page), page_size: String(pageSize) }, { replace: true });
  }, [page, pageSize, setSearchParams]);

  const { data, isLoading, isError, isFetching, refetch } = useWafCustomPages(serviceId, {
    page,
    page_size: pageSize,
  });

  const rows = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, data?.pagination?.total_pages ?? 1);

  // Client-side filter over the current page (the backend has no search/filter).
  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (typeFilter === 'all' || r.type === typeFilter) &&
        (!q || r.name.toLowerCase().includes(q)),
    );
  }, [rows, debouncedSearch, typeFilter]);

  const hasClientFilter = !!debouncedSearch.trim() || typeFilter !== 'all';

  const columns = useMemo<ColumnDef<WafCustomPage, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
            <FileText size={14} color="var(--vn-fg-muted)" />
            {row.original.name || muted('—')}
          </span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone="neutral" appearance="subtle" size="sm">
            {customPageTypeLabel(row.original.type)}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => <CustomPageActionsCell serviceId={serviceId} page={row.original} />,
      },
    ],
    [serviceId],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<FileText size={22} />}
        title={`Custom Pages${total ? ` (${total})` : ''}`}
        description="Reusable error/challenge pages served on block, error, rate-limit, and challenge events."
        actions={
          <Badge tone={total >= QUOTA_MAX ? 'warning' : 'neutral'} appearance="subtle">
            Quota used: {total} / {QUOTA_MAX}
          </Badge>
        }
      />

      <div className={styles.toolbar}>
        <div className={styles.searchGroup}>
          <Input
            wrapperClassName={styles.search}
            placeholder="Search by name"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger aria-label="Filter by type" className={styles.typeFilter}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {CUSTOM_PAGE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={styles.actions}>
          <Button
            variant="secondary"
            startIcon={<RotateCcw size={15} />}
            onClick={() => refetch()}
            aria-label="Refresh custom pages list"
          >
            Refresh
          </Button>
          <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
            New page
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : isError ? (
        <Alert
          tone="danger"
          title="Couldn't load custom pages"
          actions={<button onClick={() => refetch()}>Retry</button>}
        >
          The WAF service couldn't return its custom pages. Try again.
        </Alert>
      ) : total === 0 ? (
        <EmptyState
          icon={<FileText size={20} />}
          title="No custom pages yet"
          description="Create a custom page to serve on block, error, or challenge events."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
              New page
            </Button>
          }
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={filtered}
            getRowId={(r) => String(r.id)}
            emptyState="No custom pages match your search or filter."
            footer={`Showing ${filtered.length} of ${total}`}
          />
          {(pageCount > 1 || PAGE_SIZE_OPTIONS.length > 1) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-4)',
                marginTop: 'var(--vn-space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <label
                  htmlFor="waf-cp-page-size"
                  style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}
                >
                  Rows per page
                </label>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger id="waf-cp-page-size" size="sm" style={{ width: 72 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageCount > 1 && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={isFetching ? 'Loading…' : `Page ${page} of ${pageCount}`}
                />
              )}
            </div>
          )}
          {hasClientFilter && filtered.length === 0 && rows.length > 0 && (
            <div style={{ marginTop: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Filters apply to the current page only.
            </div>
          )}
        </>
      )}

      {createOpen && (
        <CustomPageFormDialog serviceId={serviceId} open={createOpen} onOpenChange={setCreateOpen} />
      )}
    </Container>
  );
}

export default WafCustomPageList;
